import { IconButton } from '@nike/eds'
import { type ColumnFiltersState, type SortingState } from '@tanstack/react-table'
import { exportGradingInstructionsCsv, useCreateGradingInstructionsMutation, useDeleteGradingInstructionsMutation, useSearchGradingInstructionsQuery, useUpdateGradingInstructionsMutation } from 'api/instruction'
import { ConfirmationModal } from 'components/confirmation-modal'
import { ActionLocationType, TableView } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { usePaginatedData } from 'components/tableview/usePaginatedData'
import { useTableState } from 'components/tableview/useTableState'
import { useEffect, useMemo, useState } from 'react'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch, useAppSelector } from 'redux/store'
import { GradingInstruction, type QueryResult } from 'types'

import { GradingInstructionEditor } from './components'
import { AuthRole } from 'auth/const'

const defaultSorting = [{ desc: true, id: 'lastModified' }]

export const GradingInstructionOverview = () => {
  const user = useAppSelector((state) => state.globalState.user)

  const tableState = useTableState(defaultSorting)
  const columnFiltersTransformer = (filters: ColumnFiltersState): ColumnFiltersState => filters.map(filter => {
    if (filter.id === 'style') {
      return { id: 'productCode', value: filter.value }
    } else if (filter.id === 'description') {
      return { id: 'label', value: filter.value }
    }
    return filter
  })
  const sortingTransformer = (sorting: SortingState): SortingState => sorting.map(sort => {
    if (sort.id === 'style') {
      return { desc: sort.desc, id: 'productCode' }
    } else if (sort.id === 'description') {
      return { desc: sort.desc, id: 'label' }
    }
    return sort
  })
  const { data, loading } = usePaginatedData<QueryResult<GradingInstruction>>(useSearchGradingInstructionsQuery, tableState.pagination, tableState.columnFilters, tableState.sorting, undefined, columnFiltersTransformer, sortingTransformer)
  const FILTER_INPUT_DELAY_MS = 500

  const [createGradingInstructions, { isSuccess: createSuccessful, isLoading: creatingInstruction }] = useCreateGradingInstructionsMutation()
  const [updateGradingInstructions, { isSuccess: updateSuccessful, isLoading: updatingInstruction }] = useUpdateGradingInstructionsMutation()
  const [deleteGradingInstructions] = useDeleteGradingInstructionsMutation()
  const [selectedGradingInstruction, setSelectedGradingInstruction] = useState<GradingInstruction | null>(null)
  const [modal, setModal] = useState<'Create' | 'Edit'>('Create')
  const [toBeDeleted, setToBeDeleted] = useState<number | null>(null)
  const [exporting, setExporting] = useState(false)

  const columns = useMemo(() => [
    { id: 'style', header: 'Style', type: ColumnType.TEXT, columnWidth: 4, enableColumnFilter: true, enableSorting: true },
    { id: 'color', header: 'Color', type: ColumnType.TEXT, columnWidth: 4, enableColumnFilter: false, enableSorting: false },
    { id: 'description', header: 'Description', type: ColumnType.TEXT, columnWidth: 12, enableColumnFilter: true, enableSorting: true },
    { id: 'acceptableGrades', header: 'Allowed grades', type: ColumnType.ARRAY, columnWidth: 4, enableColumnFilter: false, enableSorting: false },
    { id: 'modifiedBy', header: 'Modified by', type: ColumnType.TEXT, columnWidth: 8, enableColumnFilter: true, enableSorting: true },
    { id: 'lastModified', header: 'Last Modified', type: ColumnType.DATE, columnWidth: 8, enableColumnFilter: false, enableSorting: true }
  ], [])

  const onSaveInstruction = (gradingInstruction: GradingInstruction) => {
    modal === 'Create' ? createGradingInstructions(gradingInstruction) : updateGradingInstructions(gradingInstruction)
  }

  const onCreate = () => {
    setModal('Create')
    setSelectedGradingInstruction(new GradingInstruction())
  }

  const onEdit = (row: GradingInstruction) => {
    setModal('Edit')
    setSelectedGradingInstruction(row)
  }

  const onDelete = (id: number) => {
    deleteGradingInstructions(id)
      .catch(() => {
        dispatch(showSnackbar({
          message: 'Failed to delete instruction',
          status: 'error'
        }))
      }).finally(() => {
        setToBeDeleted(null)
      })
  }

  const onCopy = (row: GradingInstruction) => {
    setModal('Create')
    const copy = new GradingInstruction()
    copy.description = row.description
    copy.instruction = row.instruction
    copy.acceptableGrades = row.acceptableGrades
    setSelectedGradingInstruction(copy)
  }

  const onExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      const transformedFilters = columnFiltersTransformer(tableState.columnFilters)
      for (const filter of transformedFilters) {
        if (filter.value) params[filter.id] = String(filter.value)
      }
      const transformedSort = sortingTransformer(tableState.sorting)
      if (transformedSort.length > 0) {
        params.sortBy = transformedSort[0].id
        params.direction = transformedSort[0].desc ? 'DESC' : 'ASC'
      }
      await exportGradingInstructionsCsv(params)
    } catch {
      dispatch(showSnackbar({ message: 'Failed to export instructions', status: 'error' }))
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (createSuccessful || updateSuccessful) {
      setSelectedGradingInstruction(null)
    }
  }, [createSuccessful, updateSuccessful])

  const rowButtons = user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION)
    ? [
      {
        label: 'Edit',
        icon: 'Edit',
        hoverText: 'Edit',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onEdit(row) }
      },
      {
        label: 'Delete',
        icon: 'Delete',
        hoverText: 'Delete',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { setToBeDeleted(row.id) }
      },
      {
        label: 'Copy',
        icon: 'CopyPaste',
        hoverText: 'Copy',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onCopy(row) }
      }
    ]
    : [
      {
        label: 'View',
        icon: 'Show',
        hoverText: 'View Special Insctruction',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onEdit(row) }
      }
    ]

  return (
    <>
      <div className="flex justify-end m-2 mb-[-0.8rem] gap-2">
        <IconButton
          icon="Download"
          onClick={onExport}
          label={''}
          title='Export Special Instructions to CSV'
          variant="secondary"
          size="medium"
          disabled={exporting}>
        </IconButton>
        {user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION) && (
          <IconButton
            icon="CreateRule"
            onClick={onCreate}
            label={''}
            title='Create new instruction'
            variant="secondary"
            size="medium">
          </IconButton>
        )}
      </div>
      <TableView
        columns={columns}
        data={data?.results ?? []}
        rowCount={data?.totalCount ?? 0}
        textFilterDelay={FILTER_INPUT_DELAY_MS}
        tableState={tableState}
        loading={loading}
        actions={rowButtons}
        actionLocation={ActionLocationType.LAST}
        actionsWidth={48}
        defaultSorting={defaultSorting}
      />
      {selectedGradingInstruction &&
        <GradingInstructionEditor
          gradingInstruction={selectedGradingInstruction}
          mode={modal}
          onSave={onSaveInstruction}
          onDismiss={() => { setSelectedGradingInstruction(null) }}
          onLoading={creatingInstruction || updatingInstruction} />
      }
      {toBeDeleted && <ConfirmationModal
        onTrigger={() => { onDelete(toBeDeleted) }}
        onCancel={() => { setToBeDeleted(null) }}
        title="Delete instruction"
        message="Are you sure you want to delete this instruction?"
        iconClass=""
        confirmText="Confirm"
      />}
    </>
  )
}
